/**
 * Suggestions API - Direct Supabase Queries
 *
 * This module handles all suggestion-related database operations using
 * direct Supabase queries instead of FastAPI endpoints.
 */

import { supabaseClient } from 'src/db/supabase';
import { autoSuggestionsLogger } from '@annote/core/utils/debug';
import type { SuggestionGroupWithSuggestions, SuggestionAcceptanceMethod } from '@annote/types';
import { isUUID } from '@annote/types';

/**
 * Fetch multiple suggestion groups with all their suggestions in one query
 * @param ids - Array of suggestion group UUIDs
 * @param retries - Number of retry attempts (default: 3) - handles eventual consistency
 */
export const fetchSuggestionGroups = async (
  ids: string[],
  retries: number = 3,
): Promise<SuggestionGroupWithSuggestions[]> => {
  if (ids.length === 0) return [];

  // Filter out invalid UUIDs to prevent 400 errors from Supabase
  const validIds = ids.filter((id) => {
    if (!id || typeof id !== 'string') {
      autoSuggestionsLogger.warn('Invalid ID (not a string):', id);
      return false;
    }
    if (!isUUID(id)) {
      autoSuggestionsLogger.warn('Invalid UUID format:', id);
      return false;
    }
    return true;
  });

  if (validIds.length === 0) {
    autoSuggestionsLogger.error('No valid UUIDs provided. Original IDs:', ids);
    return [];
  }

  if (validIds.length !== ids.length) {
    autoSuggestionsLogger.warn(`Filtered out ${ids.length - validIds.length} invalid UUID(s). Valid IDs:`, validIds);
  }

  autoSuggestionsLogger.debug('Fetching groups with IDs:', validIds);

  // Get current user ID - explicitly filter by userId for reliability (consistent with initial query)
  const {
    data: { user },
  } = await supabaseClient.auth.getUser();
  if (!user) {
    console.error('[fetchSuggestionGroups] No authenticated user');
    return [];
  }

  // Retry logic to handle eventual consistency (groups may not be immediately visible after creation)
  // This handles cases where:
  // 1. Database transaction hasn't committed yet
  // 2. Read replica lag (if using replicas)
  // 3. RLS policy evaluation timing
  let groupsData: Array<Record<string, unknown>> | null = null;
  for (let attempt = 0; attempt < retries; attempt += 1) {
    // Try to fetch groups - explicitly filter by userId for consistency with initial query
    // eslint-disable-next-line no-await-in-loop -- Intentional sequential retries with exponential backoff delays
    const { data, error: groupsError } = await supabaseClient
      .from('suggestion_groups')
      .select('*')
      .in('id', validIds)
      .eq('userId', user.id);

    if (groupsError) {
      // Log detailed error information for debugging
      autoSuggestionsLogger.error('Database error fetching groups:', {
        error: groupsError,
        message: groupsError.message,
        details: groupsError.details,
        hint: groupsError.hint,
        code: groupsError.code,
        requestedIds: validIds,
        attempt: attempt + 1,
      });
      // Don't throw - return empty array to prevent user-facing errors
      // The error is already logged for debugging
      return [];
    }

    if (data && data.length > 0) {
      // Found groups, use this data
      groupsData = data;
      autoSuggestionsLogger.debug(`Found ${data.length} groups on attempt ${attempt + 1}`);
      break;
    }

    // No groups found - if this is not the last attempt, wait and retry
    if (attempt < retries - 1) {
      const delayMs = 2 ** attempt * 100; // Exponential backoff: 100ms, 200ms, 400ms
      autoSuggestionsLogger.warn(
        `No groups found (attempt ${attempt + 1}/${retries}) for IDs: ${validIds.join(
          ', ',
        )}. Retrying in ${delayMs}ms...`,
      );
      // Wait before retrying (don't return timer ID from Promise executor)
      // eslint-disable-next-line no-await-in-loop -- Intentional delay between retries
      await new Promise<void>((resolve) => {
        setTimeout(() => {
          resolve();
        }, delayMs);
      });
    } else {
      // Last attempt failed - log detailed error info
      autoSuggestionsLogger.error('No groups found after all retries:', {
        requestedIds: validIds,
        attempts: retries,
        // Check if we can query ANY groups for this user to verify RLS is working
      });
      return [];
    }
  }

  if (!groupsData || groupsData.length === 0) {
    autoSuggestionsLogger.warn('No groups found for IDs:', validIds);
    return [];
  }

  autoSuggestionsLogger.debug('Found groups:', groupsData.length);

  // Now fetch suggestions for these groups
  const groupIds = groupsData.map((g) => g.id);
  const { data: suggestionsData, error: suggestionsError } = await supabaseClient
    .from('suggestions')
    .select('*')
    .in('group_id', groupIds);

  if (suggestionsError) {
    autoSuggestionsLogger.error('Database error fetching suggestions:', suggestionsError);
    throw suggestionsError;
  }

  // Combine groups with their suggestions
  const result = groupsData.map((group) => ({
    ...group,
    suggestions: (suggestionsData || []).filter((s) => s.group_id === group.id),
  })) as SuggestionGroupWithSuggestions[];

  autoSuggestionsLogger.debug('Successfully fetched groups:', {
    requested: validIds.length,
    found: result.length,
    groupIds: result.map((g) => g.id),
    totalSuggestions: result.reduce((sum, g) => sum + (g.suggestions?.length || 0), 0),
  });

  return result;
};

/**
 * Accept one or more suggestions
 * @param suggestionIds - Array of suggestion UUIDs
 * @param acceptanceMethod - How the suggestions were accepted
 * @returns void
 */
export const acceptSuggestions = async (
  suggestionIds: string[],
  acceptanceMethod: Extract<SuggestionAcceptanceMethod, 'individual' | 'group_accept_all'> = 'individual',
): Promise<void> => {
  if (suggestionIds.length === 0) return;

  const now = new Date().toISOString();

  // Update all suggestions to accepted in a single batch
  // Note: response_time_seconds is auto-calculated by DB trigger
  // Note: group status is auto-updated by DB trigger when all suggestions are done
  const { error } = await supabaseClient
    .from('suggestions')
    .update({
      status: 'accepted',
      responded_at: now,
      acceptance_method: acceptanceMethod,
    })
    .in('id', suggestionIds);

  if (error) throw error;
};

/**
 * Reject one or more suggestions
 * @param suggestionIds - Array of suggestion UUIDs
 * @param acceptanceMethod - How the suggestions were rejected
 * @returns void
 */
export const rejectSuggestions = async (
  suggestionIds: string[],
  // Allow dismissing individual suggestions as well as group rejections.
  // If method is 'dismissed' we'll set the suggestion status to 'dismissed'
  acceptanceMethod: Extract<SuggestionAcceptanceMethod, 'individual' | 'group_reject_all' | 'dismissed'> = 'individual',
): Promise<void> => {
  if (suggestionIds.length === 0) return;

  const now = new Date().toISOString();

  // Determine the status to write based on the acceptanceMethod
  const statusToWrite = acceptanceMethod === 'dismissed' ? 'dismissed' : 'rejected';

  // Update all suggestions in a single batch
  // Note: response_time_seconds is auto-calculated by DB trigger
  // Note: group status is auto-updated by DB trigger when all suggestions are done
  const { error } = await supabaseClient
    .from('suggestions')
    .update({
      status: statusToWrite,
      responded_at: now,
      acceptance_method: acceptanceMethod,
    })
    .in('id', suggestionIds);

  if (error) throw error;
};

/**
 * Accept all pending suggestions in a group
 * @param groupId - Suggestion group UUID
 */
export const acceptSuggestionGroup = async (groupId: string): Promise<void> => {
  const now = new Date().toISOString();

  // Get all pending suggestion IDs in group
  const { data: suggestions, error: fetchError } = await supabaseClient
    .from('suggestions')
    .select('id')
    .eq('group_id', groupId)
    .eq('status', 'pending');

  if (fetchError) throw fetchError;
  if (!suggestions || suggestions.length === 0) return;

  // Update all suggestions to accepted
  // Note: response_time_seconds is auto-calculated by DB trigger
  const { error: updateError } = await supabaseClient
    .from('suggestions')
    .update({
      status: 'accepted',
      responded_at: now,
      acceptance_method: 'group_accept_all',
    })
    .in(
      'id',
      suggestions.map((s: { id: string }) => s.id),
    );

  if (updateError) throw updateError;

  // Update group status manually (trigger only updates when ALL are done)
  // Note: response_time_seconds is auto-calculated by DB trigger
  const { error: groupError } = await supabaseClient
    .from('suggestion_groups')
    .update({
      status: 'accepted',
      responded_at: now,
      acceptance_method: 'accept_all',
    })
    .eq('id', groupId);

  if (groupError) throw groupError;
};

/**
 * Reject all pending suggestions in a group
 * @param groupId - Suggestion group UUID
 */
export const rejectSuggestionGroup = async (groupId: string): Promise<void> => {
  const now = new Date().toISOString();

  const { data: suggestions, error: fetchError } = await supabaseClient
    .from('suggestions')
    .select('id')
    .eq('group_id', groupId)
    .eq('status', 'pending');

  if (fetchError) throw fetchError;
  if (!suggestions || suggestions.length === 0) return;

  // Update all suggestions to rejected
  // Note: response_time_seconds is auto-calculated by DB trigger
  const { error: updateError } = await supabaseClient
    .from('suggestions')
    .update({
      status: 'rejected',
      responded_at: now,
      acceptance_method: 'group_reject_all',
    })
    .in(
      'id',
      suggestions.map((s: { id: string }) => s.id),
    );

  if (updateError) throw updateError;

  // Update group status manually (trigger only updates when ALL are done)
  // Note: response_time_seconds is auto-calculated by DB trigger
  const { error: groupError } = await supabaseClient
    .from('suggestion_groups')
    .update({
      status: 'rejected',
      responded_at: now,
      acceptance_method: 'reject_all',
    })
    .eq('id', groupId);

  if (groupError) throw groupError;
};

/**
 * Dismiss a suggestion group before it's been viewed
 * Used when user clicks X on an auto-suggestion in the AnniBar
 * @param groupId - Suggestion group UUID
 */
export const dismissSuggestionGroup = async (groupId: string): Promise<void> => {
  const now = new Date().toISOString();

  const { data: suggestions, error: fetchError } = await supabaseClient
    .from('suggestions')
    .select('id')
    .eq('group_id', groupId)
    .eq('status', 'pending');

  if (fetchError) throw fetchError;
  if (!suggestions || suggestions.length === 0) return;

  // Update all suggestions to dismissed
  const { error: updateError } = await supabaseClient
    .from('suggestions')
    .update({
      status: 'dismissed',
      responded_at: now,
      acceptance_method: 'dismissed',
    })
    .in(
      'id',
      suggestions.map((s: { id: string }) => s.id),
    );

  if (updateError) throw updateError;

  // Update group status
  const { error: groupError } = await supabaseClient
    .from('suggestion_groups')
    .update({
      status: 'dismissed',
      responded_at: now,
      acceptance_method: 'dismissed',
    })
    .eq('id', groupId);

  if (groupError) throw groupError;
};
